import React, { useState } from 'react';
import { useCompany } from '../context/CompanyContext';

/**
 * Reusable Company Logo Component
 * Automatically uses uploaded corporate logo from database, with styled corporate fallback.
 * 
 * Variants:
 * - 'sidebar' (default): Optimized for 240px left sidebar
 * - 'login': Prominent display for login screen
 * - 'header': Compact display for topbars
 * - 'proposal': Official look for Proposal Document & PDF Print
 * - 'small': Compact square/icon sizing
 */
export default function CompanyLogo({ 
  variant = 'sidebar', 
  className = '', 
  style = {},
  showSubtitle = true,
  alt = 'Company Logo' 
}) {
  const { activeLogo, hasCustomLogo, companyName, companySubtitle } = useCompany();
  const [imageError, setImageError] = useState(false);

  // If a custom logo image is available and hasn't errored
  if (hasCustomLogo && !imageError) {
    let containerStyle = { display: 'flex', alignItems: 'center' };
    let imgStyle = { objectFit: 'contain', display: 'block' };

    switch (variant) {
      case 'sidebar':
        containerStyle = {
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          width: '100%',
          padding: '0.25rem 0'
        };
        imgStyle = {
          maxHeight: '48px',
          maxWidth: '100%',
          objectFit: 'contain',
          filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.05))'
        };
        break;

      case 'login':
        containerStyle = {
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
          marginBottom: '1.5rem',
          padding: '0.5rem 0',
          backgroundColor: 'transparent'
        };
        imgStyle = {
          maxHeight: '76px',
          maxWidth: '280px',
          objectFit: 'contain'
        };
        break;

      case 'proposal':
        containerStyle = {
          display: 'flex',
          alignItems: 'center',
          gap: '1rem'
        };
        imgStyle = {
          maxHeight: '52px',
          maxWidth: '220px',
          objectFit: 'contain'
        };
        break;

      case 'header':
        containerStyle = { display: 'flex', alignItems: 'center' };
        imgStyle = { maxHeight: '36px', maxWidth: '160px', objectFit: 'contain' };
        break;

      case 'small':
        containerStyle = { display: 'inline-flex', alignItems: 'center' };
        imgStyle = { maxHeight: '28px', maxWidth: '100px', objectFit: 'contain' };
        break;

      default:
        imgStyle = { maxHeight: '48px', maxWidth: '100%', objectFit: 'contain' };
    }

    return (
      <div className={`company-logo-container ${className}`} style={{ ...containerStyle, ...style }}>
        <img 
          src={activeLogo} 
          alt={alt || companyName} 
          style={imgStyle}
          onError={() => setImageError(true)}
          className="company-logo-img"
        />
      </div>
    );
  }

  // --- Fallback Styled Vector Mark if no image uploaded or image load fails ---
  if (variant === 'login') {
    return (
      <div className={`company-logo-fallback ${className}`} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '1.75rem', width: '100%', ...style }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <h1 style={{ color: '#eb0a1e', fontSize: '2.5rem', fontWeight: 900, margin: 0, lineHeight: 1, letterSpacing: '-1.5px', fontFamily: "'Helvetica Neue', Arial, sans-serif" }}>
            TOYOTA
          </h1>
        </div>
        <div style={{ height: '3px', backgroundColor: '#e5e7eb', margin: '8px 0', width: '220px', borderRadius: '2px' }}></div>
        {showSubtitle && (
          <p style={{ fontSize: '0.68rem', color: '#1f2937', margin: 0, fontWeight: 800, letterSpacing: '0.4px', textAlign: 'center', lineHeight: 1.4 }}>
            {companyName || 'TOYOTA MATERIAL HANDLING'}<br/>
            <span style={{ fontSize: '0.62rem', color: '#4b5563', fontWeight: 700 }}>{companySubtitle || 'WAREHOUSE SOLUTIONS THAILAND'}</span>
          </p>
        )}
      </div>
    );
  }

  if (variant === 'proposal') {
    return (
      <div className={`company-logo-fallback ${className}`} style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', ...style }}>
        <h1 style={{ color: '#eb0a1e', fontSize: '2.2rem', fontWeight: 900, margin: 0, lineHeight: 1, letterSpacing: '-1px', fontFamily: "'Helvetica Neue', Arial, sans-serif" }}>
          TOYOTA
        </h1>
        <div style={{ height: '2px', backgroundColor: '#c8102e', margin: '4px 0', width: '100%', borderRadius: '1px' }}></div>
        {showSubtitle && (
          <span style={{ fontSize: '0.65rem', color: '#374151', fontWeight: 800, letterSpacing: '0.5px' }}>
            {companyName}
          </span>
        )}
      </div>
    );
  }

  // Default / Sidebar Fallback
  return (
    <div className={`sidebar-logo ${className}`} style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%', ...style }}>
      <h1 style={{ color: '#eb0a1e', fontSize: '2.4rem', fontWeight: 900, margin: 0, lineHeight: 1, letterSpacing: '-1.5px', fontFamily: "'Helvetica Neue', Arial, sans-serif" }}>
        TOYOTA
      </h1>
      <div style={{ height: '3px', backgroundColor: '#9ca3af', margin: '6px 0', width: '100%', borderRadius: '2px', opacity: 0.7 }}></div>
      {showSubtitle && (
        <p style={{ fontSize: '0.65rem', color: '#1f2937', margin: 0, fontWeight: 800, letterSpacing: '0.4px', textAlign: 'left', lineHeight: 1.4 }}>
          TOYOTA MATERIAL HANDLING<br/>
          <span style={{ fontSize: '0.6rem', color: '#4b5563', fontWeight: 700 }}>WAREHOUSE SOLUTIONS THAILAND</span>
        </p>
      )}
    </div>
  );
}
